import { useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

/**
 * Custom hook for chat API operations
 * Centralizes all API calls for ChatPage
 */
export const useChatApi = (state, user, navigate) => {
  const INITIAL_MESSAGES_LIMIT = 20;
  const HISTORY_MESSAGES_LIMIT = 50;

  const {
    setRooms, selectedRoom, setSelectedRoom, setMessages, setMembers,
    setPinnedMessages, setRoomTheme, setPendingJoinRoom, setMobileMenuOpen,
    setFriends, setFriendRequests, setBlockedUsers, setPmRequests, setStaffList,
    setGames, setUnreadPmCount, setUnreadBySender, setAdminNotifications,
    setGlobalTheme, setUserSearchResults, setUserSearchQuery,
    messages, pmMessages, setPmMessages, selectedFriend,
    wsRef, newRoomData, setNewRoomData, setCreateRoomOpen,
    newRoomName, newRoomDescription, isPrivateRoom,
    setNewRoomName, setNewRoomDescription, setIsPrivateRoom, setActiveTab,
    setIsLoadingMore, setHasMoreMessages
  } = state;

  // Load rooms from API
  const loadRooms = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(response.data);
      return response.data;
    } catch (error) {
      toast.error('Failed to load rooms');
      return [];
    }
  }, [setRooms]);

  // Load friends and related data
  const loadFriends = useCallback(async () => {
    if (user.role === 'guest') {
      setFriends([]);
      setFriendRequests([]);
      setBlockedUsers([]);
      setPmRequests([]);
      setStaffList([]);
      return;
    }
    try {
      const promises = [
        axios.get(`${API}/friends`),
        axios.get(`${API}/friends/requests`),
        axios.get(`${API}/blocked`),
        axios.get(`${API}/pm/requests`),
      ];
      
      if (user.role === 'premium' || user.role === 'staff' || user.role === 'super_admin') {
        promises.push(axios.get(`${API}/staff/list`));
      }
      
      const results = await Promise.all(promises);
      setFriends(results[0].data);
      setFriendRequests(results[1].data);
      setBlockedUsers(results[2].data);
      setPmRequests(results[3].data);
      
      if (results[4]) {
        setStaffList(results[4].data);
      }
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  }, [user.role, setFriends, setFriendRequests, setBlockedUsers, setPmRequests, setStaffList]);

  // Load friend requests
  const loadFriendRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/friends/requests`);
      setFriendRequests(response.data);
    } catch (error) {
      console.error('Failed to load friend requests:', error);
    }
  }, [setFriendRequests]);

  // Load games
  const loadGames = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/games`);
      setGames(response.data);
    } catch (error) {
      console.error('Failed to load games:', error);
    }
  }, [setGames]);

  // Fetch unread PM count
  const fetchUnreadPmCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pm/unread-count`);
      setUnreadPmCount(response.data.total_unread);
      setUnreadBySender(response.data.unread_by_sender || []);
    } catch (error) {
      console.error('Failed to fetch unread PM count:', error);
    }
  }, [setUnreadPmCount, setUnreadBySender]);

  // Load admin notifications
  const loadAdminNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/active`);
      setAdminNotifications(response.data || []);
    } catch (error) {
      console.error('Failed to load admin notifications:', error);
      setAdminNotifications([]);
    }
  }, [setAdminNotifications]);

  // Load global theme
  const loadGlobalTheme = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/theme/global`);
      setGlobalTheme(response.data);
    } catch (error) {
      console.error('Failed to load global theme:', error);
    }
  }, [setGlobalTheme]);

  // Handle explicit room join
  const handleJoinRoom = useCallback(async (room) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/rooms/${room.id}/join`, {}, { headers });
      toast.success(`Joined ${room.name} successfully!`);
      
      setPendingJoinRoom(null);
      setMobileMenuOpen(false);
      
      const updatedRoom = { ...room, is_member: true };
      setRooms(prevRooms => 
        prevRooms.map(r => r.id === room.id ? { ...r, is_member: true } : r)
      );
      
      setSelectedRoom(updatedRoom);
      setMessages([]);
      setMembers([]);
      
      const [messagesRes, membersRes] = await Promise.all([
        axios.get(`${API}/rooms/${room.id}/messages?limit=${INITIAL_MESSAGES_LIMIT}`, { headers }),
        axios.get(`${API}/rooms/${room.id}/members`, { headers })
      ]);
      
      setMessages(messagesRes.data);
      setMembers(membersRes.data);
      
      const roomsResponse = await axios.get(`${API}/rooms`, { headers });
      setRooms(roomsResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join room');
    }
  }, [setPendingJoinRoom, setMobileMenuOpen, setRooms, setSelectedRoom, setMessages, setMembers]);

  // Select room
  const selectRoom = useCallback(async (room) => {
    setSelectedRoom(room);
    setMessages([]);
    state.setHasMoreMessages(true);
    setMembers([]);
    setPendingJoinRoom(null);
    
    // If the user is not a member of this room, call handleJoinRoom
    // This will ensure the backend adds them to db.room_members if they click a room they're not in
    if (!room.is_member) {
      try {
        await handleJoinRoom(room);
      } catch (error) {
        console.error("Error calling handleJoinRoom from selectRoom:", error);
        toast.error("Failed to join room automatically.");
        setSelectedRoom(null); // Clear selected room if auto-join fails
        return;
      }
    }

    // Send room focus message via WebSocket for presence tracking
    const ws = wsRef?.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'room_focus',
        room_id: room.id,
        is_focused: true
      }));
    }
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      try {
        const messagesPromise = axios.get(
          `${API}/rooms/${room.id}/messages?limit=${INITIAL_MESSAGES_LIMIT}`,
          { headers }
        );
        const membersPromise = axios.get(`${API}/rooms/${room.id}/members`, { headers });
        const onlinePromise = axios.get(`${API}/rooms/${room.id}/online`, { headers });

        const messagesRes = await messagesPromise;
        setMessages(messagesRes.data);

        Promise.allSettled([membersPromise, onlinePromise]).then(([membersRes, onlineRes]) => {
          if (membersRes.status !== 'fulfilled') return;

          const membersData = membersRes.value.data || [];
          const onlineUserIds =
            onlineRes.status === 'fulfilled'
              ? (onlineRes.value.data.online_users?.map(u => u.id) || [])
              : [];

          const membersWithOnline = membersData.map(m => ({
            ...m,
            online: onlineUserIds.includes(m.user_id || m.user?.id)
          }));
          setMembers(membersWithOnline);
        });
        
        Promise.allSettled([
          axios.get(`${API}/rooms/${room.id}`, { headers }),
          axios.get(`${API}/rooms/${room.id}/pinned`, { headers }),
          axios.get(`${API}/rooms/${room.id}/theme`, { headers })
        ]).then(([roomRes, pinnedRes, themeRes]) => {
          if (roomRes.status === 'fulfilled') {
            setSelectedRoom(prev => ({ ...prev, ...roomRes.value.data }));
          }
          if (pinnedRes.status === 'fulfilled') {
            setPinnedMessages(pinnedRes.value.data);
          }
          if (themeRes.status === 'fulfilled') {
            setRoomTheme(themeRes.value.data || { font_family: '', font_color: '#000000' });
          }
        });
      } catch (loadError) {
        // If user is not member and it's not a global chat, suggest joining
        if (loadError.response?.status === 403 && !room.is_member) {
          setPendingJoinRoom(room);
          setSelectedRoom(null);
          return;
        }
        throw loadError;
      }
    } catch (error) {
      console.error('Error loading room:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have access to this room');
        setSelectedRoom(null);
      }
    }
  }, [setSelectedRoom, setMessages, setMembers, setPendingJoinRoom, setPinnedMessages, setRoomTheme, wsRef, handleJoinRoom]);

  // Create room
  const createRoom = useCallback(async () => {
    try {
      await axios.post(`${API}/rooms`, newRoomData);
      toast.success('Room created successfully!');
      setCreateRoomOpen(false);
      setNewRoomData({ name: '', type: 'public', description: '' });
      loadRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create room');
    }
  }, [newRoomData, setCreateRoomOpen, setNewRoomData, loadRooms]);

  // Mobile room creation
  const createRoomMobile = useCallback(async () => {
    if (!newRoomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }
    try {
      await axios.post(`${API}/rooms`, {
        name: newRoomName,
        description: newRoomDescription,
        type: isPrivateRoom ? 'private' : 'public'
      });
      toast.success('Room created successfully!');
      setNewRoomName('');
      setNewRoomDescription('');
      setIsPrivateRoom(false);
      setActiveTab('rooms');
      loadRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create room');
    }
  }, [newRoomName, newRoomDescription, isPrivateRoom, setNewRoomName, setNewRoomDescription, setIsPrivateRoom, setActiveTab, loadRooms]);

  // Search users
  const searchUsers = useCallback(async (query) => {
    if (!query?.trim()) return;
    try {
      const response = await axios.get(`${API}/users/search?q=${query}`);
      setUserSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    }
  }, [setUserSearchResults]);

  // Send friend request
  const sendFriendRequest = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/friends/request/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Friend request sent!');
      setUserSearchResults([]);
      setUserSearchQuery('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  }, [setUserSearchResults, setUserSearchQuery]);

  // Accept friend request
  const acceptFriendRequest = useCallback(async (friendshipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/friends/accept/${friendshipId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Friend request accepted!');
      loadFriends();
      loadFriendRequests();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  }, [loadFriends, loadFriendRequests]);

  // Reject friend request
  const rejectFriendRequest = useCallback(async (friendshipId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/friends/reject/${friendshipId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Friend request rejected');
      loadFriends();
    } catch (error) {
      toast.error('Failed to reject friend request');
    }
  }, [loadFriends]);

  // Accept PM request
  const acceptPmRequest = useCallback(async (requestId) => {
    try {
      await axios.post(`${API}/pm/requests/${requestId}/accept`);
      toast.success('PM request accepted!');
      loadFriends();
    } catch (error) {
      toast.error('Failed to accept PM request');
    }
  }, [loadFriends]);

  // Reject PM request
  const rejectPmRequest = useCallback(async (requestId) => {
    try {
      await axios.post(`${API}/pm/requests/${requestId}/reject`);
      toast.success('PM request rejected');
      loadFriends();
    } catch (error) {
      toast.error('Failed to reject PM request');
    }
  }, [loadFriends]);

  // Block user
  const blockUser = useCallback(async (userId) => {
    if (!window.confirm('Are you sure you want to block this user?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/block/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User blocked');
      await loadFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    }
  }, [loadFriends]);

  // Unblock user
  const unblockUser = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/unblock/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User unblocked - they can now message you again');
      await loadFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock user');
    }
  }, [loadFriends]);

  // Unfriend user
  const unfriendUser = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/friends/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User removed from friends');
      await loadFriends();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unfriend user');
    }
  }, [loadFriends]);

  // Pin message
  const pinMessage = useCallback(async (messageId) => {
    if (!selectedRoom) return;
    try {
      await axios.post(`${API}/rooms/${selectedRoom.id}/messages/${messageId}/pin`);
      toast.success('Message pinned');
      const response = await axios.get(`${API}/rooms/${selectedRoom.id}/pinned`);
      setPinnedMessages(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pin message');
    }
  }, [selectedRoom, setPinnedMessages]);

  // Unpin message
  const unpinMessage = useCallback(async (messageId) => {
    if (!selectedRoom) return;
    try {
      await axios.delete(`${API}/rooms/${selectedRoom.id}/messages/${messageId}/pin`);
      toast.success('Message unpinned');
      const response = await axios.get(`${API}/rooms/${selectedRoom.id}/pinned`);
      setPinnedMessages(response.data);
    } catch (error) {
      toast.error('Failed to unpin message');
    }
  }, [selectedRoom, setPinnedMessages]);

  // Delete message
  const deleteMessage = useCallback(async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/rooms/${selectedRoom.id}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(messages.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      console.error('Delete message error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  }, [selectedRoom, messages, setMessages]);

  // Delete PM message
  const deletePmMessage = useCallback(async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/pm/${selectedFriend.id}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPmMessages(pmMessages.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (error) {
      console.error('Delete PM message error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }
  }, [selectedFriend, pmMessages, setPmMessages]);

  // Refresh messages
  const handleRefreshMessages = useCallback(async () => {
    if (!selectedRoom) return;
    try {
      const token = localStorage.getItem('token');
      const [messagesRes, membersRes] = await Promise.all([
        axios.get(`${API}/rooms/${selectedRoom.id}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/rooms/${selectedRoom.id}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMessages(messagesRes.data);
      setMembers(membersRes.data);
      toast.success('Messages refreshed');
    } catch (error) {
      console.error('Error refreshing messages:', error);
      toast.error('Failed to refresh messages');
    }
  }, [selectedRoom, setMessages, setMembers]);

  // Leave room
  const leaveRoom = useCallback(async (roomId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/rooms/${roomId}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Left room successfully');
      
      // Reload rooms to update the list
      const roomsResponse = await axios.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(roomsResponse.data);
      
      // If we left the currently selected room, clear selection
      if (selectedRoom?.id === roomId) {
        const globalChat = roomsResponse.data.find((r) => 
          r.name?.toLowerCase() === 'global chat' || 
          r.name?.toLowerCase() === 'public chat'
        );
        if (globalChat) {
          await selectRoom({ ...globalChat, is_member: true });
          setActiveTab('rooms');
        } else {
          setSelectedRoom(null);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave room');
    }
  }, [selectedRoom, setRooms, setSelectedRoom, selectRoom, setActiveTab]);

  // View user profile - navigates to user's profile page
  const viewProfile = useCallback((userId) => {
    // Navigate to the user's profile page
    navigate(`/profile/${userId}`);
  }, [navigate]);

  const fetchMoreMessages = useCallback(async () => {
    if (state.isLoadingMore || !state.hasMoreMessages || !selectedRoom) return;

    state.setIsLoadingMore(true);
    try {
      const oldestMessage = messages[0];
      if (!oldestMessage) {
        state.setHasMoreMessages(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/rooms/${selectedRoom.id}/messages?before=${oldestMessage.created_at}&limit=${HISTORY_MESSAGES_LIMIT}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.length > 0) {
        setMessages((prev) => [...response.data, ...prev]);
      }

      if (response.data.length < HISTORY_MESSAGES_LIMIT) {
        state.setHasMoreMessages(false);
      }
    } catch (error) {
      toast.error('Failed to load older messages');
    } finally {
      state.setIsLoadingMore(false);
    }
  }, [selectedRoom, messages, state.isLoadingMore, state.hasMoreMessages, setMessages, state.setIsLoadingMore, state.setHasMoreMessages]);

  const muteUser = useCallback(async (userId, roomId) => {
    if (!window.confirm('Are you sure you want to mute this user?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/rooms/${roomId}/mute/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User muted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mute user');
    }
  }, []);

  const kickUser = useCallback(async (userId, roomId) => {
    if (!window.confirm('Are you sure you want to kick this user from the room?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/rooms/${roomId}/kick/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User kicked successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to kick user');
    }
  }, []);

  const banUser = useCallback(async (userId, roomId) => {
    if (!window.confirm('Are you sure you want to ban this user from the room?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/rooms/${roomId}/ban/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User banned successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  }, []);

  return {
    loadRooms,
    loadFriends,
    loadFriendRequests,
    loadGames,
    fetchUnreadPmCount,
    loadAdminNotifications,
    loadGlobalTheme,
    selectRoom,
    fetchMoreMessages,
    createRoom,
    createRoomMobile,
    searchUsers,
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    acceptPmRequest,
    rejectPmRequest,
    blockUser,
    unblockUser,
    unfriendUser,
    pinMessage,
    unpinMessage,
    deleteMessage,
    deletePmMessage,
    handleRefreshMessages,
    leaveRoom,
    viewProfile,
    muteUser,
    kickUser,
    banUser
  };
};

export default useChatApi;
